import api from "./api";
import {
  InviteMemberResult,
  Workspace,
  WorkspaceCompany,
  WorkspaceInvitation,
  WorkspaceMember,
} from "@/types/workspace";
import { BillingAccount, CreditTransaction, PaginatedResponse } from "@/types/billing";
import { AxiosRequestConfig } from "axios";

interface WorkspaceApiResponse extends Omit<Workspace, "companies"> {
  companies?: WorkspaceCompany[];
}

interface UserCompanyResponse {
  company_id: number;
  company_name: string;
  status: "completed" | "in_progress" | "error" | "finalizing" | string;
  lastReportId?: number | null;
  company_url?: string | null;
  signal_count?: number | null;
  verification_status?: string | null;
}

interface ApiErrorPayload {
  code?: string;
  detail?: string;
  onboarding_required?: boolean;
  resume_step?: string;
  resume_key?: string;
}

interface CreditTransactionQueryParams {
  workspaceId?: string | number | null;
  page?: number;
  pageSize?: number;
}

const BILLING_ACCOUNT_ENDPOINT = "/billing/accounts";

const buildWorkspacePath = (workspaceId: string | number): string =>
  `/workspaces/${workspaceId}/`;

const buildWorkspaceMembersPath = (workspaceId: string | number): string =>
  `/workspaces/${workspaceId}/members/`;

const buildWorkspaceInvitationsPath = (workspaceId: string | number): string =>
  `/workspaces/${workspaceId}/invitations/`;

const parseApiErrorMessage = (error: unknown): string => {
  if (typeof error !== "object" || !error) {
    return "Request failed";
  }

  const maybeResponse = error as {
    response?: { data?: ApiErrorPayload };
    message?: string;
  };

  return (
    maybeResponse.response?.data?.detail ??
    maybeResponse.response?.data?.code ??
    maybeResponse.message ??
    "Request failed"
  );
};

const normalizeWorkspace = (workspace: WorkspaceApiResponse): Workspace => ({
  ...workspace,
  companies: workspace.companies ?? [],
});

const normalizeCompany = (company: UserCompanyResponse): WorkspaceCompany => ({
  company_id: company.company_id,
  company_name: company.company_name,
  status: company.status,
  lastReportId: company.lastReportId ?? null,
  company_url: company.company_url ?? null,
  signal_count: company.signal_count ?? null,
  verification_status: company.verification_status ?? null,
});

const getAuthConfig = (token?: string | null): AxiosRequestConfig | undefined => {
  if (!token) {
    return undefined;
  }

  return {
    headers: {
      Authorization: `JWT ${token}`,
    },
  };
};

export const getWorkspaces = async (token?: string | null): Promise<Workspace[]> => {
  const response = await api.get<WorkspaceApiResponse[]>(
    "/workspaces/",
    getAuthConfig(token),
  );
  return response.data.map(normalizeWorkspace);
};

export const createWorkspace = async (name: string): Promise<Workspace> => {
  try {
    const response = await api.post<WorkspaceApiResponse>("/workspaces/", { name });
    return normalizeWorkspace(response.data);
  } catch (error: unknown) {
    const maybeResponse = error as { response?: { data?: ApiErrorPayload } };
    const payload = maybeResponse.response?.data;

    const err: Error & {
      code?: string;
      onboardingRequired?: boolean;
      resumeStep?: string;
      resumeKey?: string;
    } = new Error(payload?.detail ?? parseApiErrorMessage(error));

    if (payload?.code) err.code = payload.code;
    if (typeof payload?.onboarding_required === "boolean") {
      err.onboardingRequired = payload.onboarding_required;
    }
    if (typeof payload?.resume_step === "string") err.resumeStep = payload.resume_step;
    if (typeof payload?.resume_key === "string") err.resumeKey = payload.resume_key;

    throw err;
  }
};

export const getBillingAccount = async (
  id: string | number,
  token?: string | null,
): Promise<BillingAccount> => {
  const response = await api.get<BillingAccount>(
    `${BILLING_ACCOUNT_ENDPOINT}/${id}/`,
    getAuthConfig(token),
  );
  return response.data;
};

export const renameWorkspace = async (
  workspaceId: string | number,
  name: string,
): Promise<Workspace> => {
  const response = await api.patch<WorkspaceApiResponse>(buildWorkspacePath(workspaceId), {
    name,
  });
  return normalizeWorkspace(response.data);
};

export const deleteWorkspace = async (
  workspaceId: string | number,
): Promise<void> => {
  await api.delete(buildWorkspacePath(workspaceId));
};

export const getWorkspaceMembers = async (
  workspaceId: string | number,
): Promise<WorkspaceMember[]> => {
  const response = await api.get<WorkspaceMember[]>(buildWorkspaceMembersPath(workspaceId));
  return response.data;
};

export const inviteWorkspaceMember = async (
  workspaceId: string | number,
  email: string,
): Promise<InviteMemberResult> => {
  try {
    const response = await api.post<WorkspaceMember | WorkspaceInvitation>(
      `${buildWorkspaceMembersPath(workspaceId)}invite/`,
      { email },
      {
        validateStatus: (status) => status >= 200 && status < 300,
      },
    );

    if (response.status === 201) {
      return {
        type: "member",
        data: response.data as WorkspaceMember,
      };
    }

    return {
      type: "invitation",
      data: response.data as WorkspaceInvitation,
    };
  } catch (error) {
    throw new Error(parseApiErrorMessage(error));
  }
};

export const removeWorkspaceMember = async (
  workspaceId: string | number,
  userId: string | number,
): Promise<void> => {
  await api.delete(`${buildWorkspaceMembersPath(workspaceId)}${userId}/`);
};

export const getWorkspaceInvitations = async (
  workspaceId: string | number,
): Promise<WorkspaceInvitation[]> => {
  const response = await api.get<WorkspaceInvitation[]>(
    buildWorkspaceInvitationsPath(workspaceId),
  );
  return response.data;
};

export const cancelWorkspaceInvitation = async (
  workspaceId: string | number,
  invitationId: string | number,
): Promise<void> => {
  await api.delete(`${buildWorkspaceInvitationsPath(workspaceId)}${invitationId}/`);
};

export const listCreditTransactions = async (
  billingAccountId: string | number,
  params?: CreditTransactionQueryParams,
): Promise<PaginatedResponse<CreditTransaction>> => {
  const response = await api.get<PaginatedResponse<CreditTransaction>>(
    `${BILLING_ACCOUNT_ENDPOINT}/${billingAccountId}/credit-transactions/`,
    {
      params: {
        ...(params?.workspaceId ? { workspace_id: params.workspaceId } : {}),
        ...(params?.page ? { page: params.page } : {}),
        ...(params?.pageSize ? { page_size: params.pageSize } : {}),
      },
    },
  );

  return response.data;
};

export const getWorkspaceCompanies = async (
  workspaceId: string | number,
  token?: string | null,
): Promise<WorkspaceCompany[]> => {
  const response = await api.get<UserCompanyResponse[]>("/user-companies/", {
    headers: {
      ...(token ? { Authorization: `JWT ${token}` } : {}),
      "X-Workspace-ID": String(workspaceId),
    },
  });

  return response.data.map(normalizeCompany);
};

export const renameWorkspaceCompany = async (
  workspaceId: string | number,
  companyId: string | number,
  companyName: string,
): Promise<void> => {
  await api.patch(
    `/user-companies/${companyId}/rename/`,
    { company_name: companyName },
    {
      headers: {
        "X-Workspace-ID": String(workspaceId),
      },
    },
  );
};

export const deleteWorkspaceCompany = async (
  workspaceId: string | number,
  companyId: string | number,
): Promise<void> => {
  await api.delete(`/user-companies/${companyId}/`, {
    headers: {
      "X-Workspace-ID": String(workspaceId),
    },
  });
};